import { Button } from '@monksflow/monks-ui';
import { Plus } from 'lucide-react';
import { PERMISSIONS } from '@inference.engine/permissions/web';
import {
  PROJECT_SORT_CHOICES,
  PROJECT_TYPE_ICONS,
  PROJECT_TYPE_MAP,
  type ProjectType,
} from '@/entities/project';
import { FilterChipPopover } from '@/shared/ui/FilterChipPopover';
import { SortChipPopover } from '@/shared/ui/SortChipPopover';
import { ScopedPermission } from '@/shared/ui';
import { SearchInput } from '@/shared/ui/SearchInput';
import { useTranslation } from 'react-i18next';

interface ProjectsToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedType: ProjectType | null;
  onTypeChange: (type: ProjectType | null) => void;
  sortId: string | null;
  onSortChange: (next: string | null) => void;
  onNewProject: () => void;
}

// `label` stays a getter rather than a plain read: this array is built at module
// evaluation, so reading the map here would freeze English at import time.
const TYPE_OPTIONS = (Object.keys(PROJECT_TYPE_MAP) as ProjectType[]).map((type) => {
  const Icon = PROJECT_TYPE_ICONS[type];
  return {
    value: type,
    get label() {
      return PROJECT_TYPE_MAP[type];
    },
    icon: <Icon className="size-4" />,
  };
});

/**
 * Toolbar for the Projects page matching Beat UI CardHeader.
 *
 * Layout: justify-between, padding 16px, search 320px fixed.
 */
export function ProjectsToolbar({
  searchValue,
  onSearchChange,
  selectedType,
  onTypeChange,
  sortId,
  onSortChange,
  onNewProject,
}: ProjectsToolbarProps) {
  const { t } = useTranslation('projects');
  const searchLabel = t('projectsList.projectsToolbar.search');

  return (
    <div data-testid="projects-toolbar" className="flex items-center justify-between p-section">
      <div className="flex items-center gap-2">
        <SearchInput
          className="w-80"
          aria-label={searchLabel}
          placeholder={searchLabel}
          value={searchValue}
          onChange={onSearchChange}
        />

        <SortChipPopover
          options={PROJECT_SORT_CHOICES}
          value={sortId}
          onChange={onSortChange}
          resetOptionLabel={t('projectsList.projectsToolbar.createdNewestFirst')}
        />

        <FilterChipPopover
          label={t('projectsList.projectsToolbar.type')}
          options={TYPE_OPTIONS}
          value={selectedType}
          onChange={onTypeChange}
          resetOptionLabel={t('projectsList.projectsToolbar.allTypes')}
        />
      </div>

      <ScopedPermission scope="workspace" permissions={[PERMISSIONS.ProjectCreate]}>
        <Button size="default" onClick={onNewProject}>
          <Plus className="size-4" />
          {t('projectsList.projectsToolbar.newProject')}
        </Button>
      </ScopedPermission>
    </div>
  );
}
